import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: { "Content-Type": "application/json" },
  timeout: 15000,
});

// Interceptor: attach Bearer token from localStorage
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("methos_token");
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Retry logic for network errors
async function retryRequest(config, retries = 2) {
  for (let i = 0; i < retries; i++) {
    await new Promise((r) => setTimeout(r, 1000 * (i + 1)));
    try {
      return await axios(config);
    } catch (e) {
      if (i === retries - 1) throw e;
      if (e.response) throw e; // server responded, don't retry
    }
  }
}

// Interceptor: handle 401 refresh + network retry
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach((p) => {
    if (error) p.reject(error);
    else p.resolve();
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Network error (no response) — retry once
    if (!error.response && !originalRequest._networkRetried) {
      originalRequest._networkRetried = true;
      try {
        return await retryRequest(originalRequest, 1);
      } catch {
        return Promise.reject(error);
      }
    }

    // 401 — try refresh
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url.includes("/auth/login") &&
      !originalRequest.url.includes("/auth/refresh")
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => api(originalRequest));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = localStorage.getItem("methos_refresh_token");
        if (!refreshToken) throw new Error("No refresh token");
        const res = await api.post("/auth/refresh", { refresh_token: refreshToken });
        if (res.data.access_token) {
          localStorage.setItem("methos_token", res.data.access_token);
        }
        processQueue(null);
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        localStorage.removeItem("methos_token");
        localStorage.removeItem("methos_refresh_token");
        window.dispatchEvent(new Event("auth:logout"));
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Algo deu errado. Tente novamente.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;
